import { computed, shallowRef, triggerRef, unref } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { subsetMetaByKeys, useTransformStore } from './helpers/useTransformStore'
import { Table } from '../types/components/data-rendering'
import { useProgressiveHash } from '../utils'
import { TaskModel } from '../models/TaskModel'
import { setDefaultConditions, setGroupSort } from './helpers/useSetDefaultConditions'
import { MaybeRef } from '@vueuse/core'
import { useCodes } from './useCodes'
import { InventoryTransactionsModel } from '../models/InventoryTransactionsModel'
import { useInventory } from './useInventory'
import { LanguageChoice } from '../i18n'
import { useStore } from '.'
import { RecapResponsesModel } from '../models/RecapReponsesModel'
import { useTimers } from './timers'
import { payloadIds } from '../synchronizations/sync'

const taskData = shallowRef<any[]>([])
export const closedIDs = [9, 22] //9 = merch, 22 = cart/instore
export const useTaskProfiles = defineStore('tasks', () => {
  const saveTasks = async () => {
    await TaskModel().writeTasks(taskData.value)
  }
  const resetStoreState = () => {
    taskData.value = []
  }
  const getTaskByTaskId = (taskId: string | number) => {
    return taskData.value.find((task) => {
      return task.task_id == taskId
    })
  }
  const addTask = (data: any) => {
    taskData.value.push(data)
  }
  const setTasks = async (data: any, options: { writeTasks?: boolean } = { writeTasks: true }) => {
    const edited = getEditedTasks.value
    if (edited && edited.length) {
      const editedTaskIds = edited.map((task) => task.task_id)
      data = data.map((originalTask: any)=> {
        if (editedTaskIds.includes(originalTask.task_id)) {
          const preservedEditedTask = edited.find((task) => task.task_id == originalTask.task_id)
          if (preservedEditedTask) {
            return preservedEditedTask
          }
        }
        return originalTask
      })
    }
    taskData.value = data
    if (options.writeTasks) {
      await saveTasks()
    }
  }
  const removeTasks = async (ids: string[]) => {
    taskData.value = taskData.value.filter((task) => !ids.includes(task.task_id))
    await saveTasks()
  }
  const taskHardwareDeductionCodes = (taskID: string | number, task_data?: any) => {
    const taskData = task_data ?? useTaskProfiles().getTaskByTaskId(taskID)
    if (!taskData) return null
    else {
      return useCodes().hardwareDeductionCode(taskData)
    }
  }
  const close = async (id: string | number, task_index?: number) => {
    const taskIndex = task_index ?? getTaskIndex(id)
    if (taskIndex >= 0) {
      //For now, all tasks when closed get a status of 22 (originally as here, from NP-555)
      // if (taskData.value[taskIndex].work_type_id == 4) {
      //   updateTask({ key: 'task_status_id', value: 9 }, { taskID: id })
      // } else {
      //   updateTask({ key: 'task_status_id', value: 22 }, { taskID: id })
      // }
      updateTask({ key: 'task_status_id', value: 22 }, { taskID: id })
      updateTask({ key: 'task_status', value: 'Completed' }, { taskID: id })
    }
  }
  /**Updates all keys on a task */
  const updateTaskByMerge = async (
    newData: { [key: string]: any },
    taskId: MaybeRef<string | number>,
    task_index?: number,
    options: { bulk_flag: boolean; overwrite_old_data: boolean } = {
      bulk_flag: false,
      overwrite_old_data: false,
    }
  ) => {
    const taskIndex = task_index ?? getTaskIndex(unref(taskId), 'task_id')
    if (taskIndex >= 0) {
      const oldTaskData = taskData.value[taskIndex]
      newData['bulk_updated'] = options.bulk_flag
      if (options.overwrite_old_data) {
        taskData.value[taskIndex] = { ...newData }
      } else {
        taskData.value[taskIndex] = { ...oldTaskData, ...newData }
      }
    }
  }
  const triggerTaskDataRef = async (options: { writeTasks?: boolean } = { writeTasks: true }) => {
    triggerRef(taskData)
    if (options.writeTasks) {
      await saveTasks()
    }
  }
  // Resets a given task and all its data
  const resetTask = async (
    taskId: MaybeRef<string | number>,
    skipUpdate = false,
    removeTimer = true
  ) => {
    const taskIndex = getTaskIndex(unref(taskId), 'task_id')
    if (taskIndex >= 0 && taskData.value[taskIndex]) {
      const task = taskData.value[taskIndex]
      //undo any transactions/balance changes
      await undoTaskInvChanges(task.task_id)
      //reset original state on task item
      if (task.original_state) {
        const resetState = { ...task.original_state }
        await updateTaskByMerge(resetState, task.task_id, undefined, {
          bulk_flag: false,
          overwrite_old_data: true,
        })

        if (!skipUpdate) {
          //trigger ref update and save data
          await triggerTaskDataRef()
        }
      }

      // Remove timer
      if (removeTimer) {
        const { removeChildByDataField } = useTimers()
        removeChildByDataField('task_id', task.task_id)

        // Clear out any old recap data. This call also removes any images for the recap responses
        await RecapResponsesModel().removeByTaskId(task.task_id)
      }
    }
  }
  /**Updates single key on task. !!Does not save the task!!*/
  const updateTask = (
    newData: {
      key: string
      value: any
    },
    searchFields: {
      taskID: MaybeRef<string | number>
      taskField?: string
    }
  ) => {
    searchFields.taskField ?? 'task_id'
    const taskIndex = getTaskIndex(unref(searchFields.taskID), searchFields.taskField!)
    if (taskIndex >= 0) {
      taskData.value[taskIndex][newData.key] = newData.value
      taskData.value[taskIndex]['edited'] = true
    }
  }
  /**Helper function to get task index. Returns -1 if task not found */
  const getTaskIndex = (value: string | number, taskField?: string) => {
    taskField = taskField ?? 'task_id'
    return taskData.value.findIndex((task: any) => task[taskField!] == value)
  }
  /**Helper function to update rai code and close task
   * if rai reason code is supplied, it will update, otherwise it is ignored
   */
  const updateRAI = async (
    taskID: string | number,
    code: string,
    rai_reason_code?: string,
    bulk_flag = false
  ) => {
    updateTask({ key: 'rai_code', value: code }, { taskID })
    //check if rai code supplied and update with that, otherwise wipe potentially stale data
    if (rai_reason_code) {
      updateTask({ key: 'rai_reason_code', value: rai_reason_code }, { taskID })
    } else {
      updateTask({ key: 'rai_reason_code', value: null }, { taskID })
    }
    updateTask({ key: 'bulk_updated', value: bulk_flag }, { taskID })

    await close(taskID)
  }

  //undo transactions associated to taskID
  const undoTaskInvChanges = async (taskID: string | number) => {
    const uuids = await InventoryTransactionsModel().getByTaskId(Number(taskID))
    if (uuids) {
      for (const uuid of uuids) {
        await useInventory().undoPartTransaction(uuid)
      }
    }
  }

  /**All task table items represented in table format with column meta */
  const taskTable = computed((): Table => {
    return {
      meta: {
        // defaults: {
        //   // sortBy: 'task_id',
        // },
        columns: {
          task_id: {
            label: {
              en_US: 'Task ID',
              fr_CA: 'ID tâche',
              es_MX: 'ID tarea',
            },
            type: 'number',
          },
          order_id: {
            label: {
              en_US: 'Order ID',
              fr_CA: 'ID ligne de commande',
              es_MX: 'Identificación de la Orden',
            },
            type: 'number',
          },
          orderline_id: {
            label: {
              en_US: 'Orderline ID',
              fr_CA: 'Identifiant de la ligne de commande',
              es_MX: 'Identificación de línea de pedido',
            },
            type: 'string',
          },
          store_id: {
            label: {
              en_US: 'Store ID',
              fr_CA: 'ID du magasin',
              es_MX: 'ID de tienda',
            },
            type: 'number',
          },
          store_name: {
            label: { en_US: 'Store Name', fr_CA: 'Nom du magasin', es_MX: 'Nombre de la tienda' },
            type: 'string',
          },
          store_number: {
            label: { en_US: 'Store Number', fr_CA: 'Numéro de magasin', es_MX: 'Número de tienda' },
            type: 'number',
          },
          order_type_id: {
            label: { en_US: 'Order Type ID', es_MX: '', fr_CA: 'Order Type ID' },
            type: 'number',
          },
          order_type: {
            label: { en_US: 'Order Type', es_MX: '', fr_CA: 'Type de commande' },
            type: 'string',
          },
          work_type_id: {
            label: { en_US: 'Work Type ID', es_MX: '', fr_CA: '' },
            type: 'number',
          },
          work_type: {
            label: { en_US: 'Work Type', es_MX: '', fr_CA: 'Type de projet' },
            type: 'string',
          },
          task_type_id: {
            label: { en_US: 'Task Type ID', es_MX: '', fr_CA: '' },
            type: 'number',
          },
          task_type: {
            label: { en_US: 'Task Type', fr_CA: 'Type de tâches', es_MX: 'Tipo de tarea' },
            type: 'string',
            options: [
              { en_US: 'Arrival', fr_CA: 'Arrivée', es_MX: '' },
              { en_US: 'Audit', fr_CA: 'Vérification', es_MX: '' },
              { en_US: 'Cut in', fr_CA: 'Insertion', es_MX: '' },
              { en_US: 'Departure', fr_CA: 'Départ', es_MX: '' },
              { en_US: 'Display Building', fr_CA: 'Assemblage de présentoir', es_MX: '' },
              { en_US: 'General Maintenance', fr_CA: 'Entretien général', es_MX: '' },
              { en_US: 'Install', fr_CA: 'Installation', es_MX: '' },
              { en_US: 'IRC', fr_CA: 'Bon à réduction instantanée', es_MX: '' },
              { en_US: 'Maintenance', fr_CA: 'Entretien', es_MX: '' },
              { en_US: 'Mystery Ship', fr_CA: 'Achat mystère', es_MX: '' },
              { en_US: 'Other', fr_CA: 'Autre', es_MX: '' },
              { en_US: 'POS', fr_CA: 'Publicité sur le lieu de vente', es_MX: '' },
              { en_US: 'Premium Holdover', fr_CA: 'Prolongation du client', es_MX: '' },
              { en_US: 'Product Replenishment', fr_CA: 'Réassort de produits', es_MX: '' },
              { en_US: 'Quick Audit', fr_CA: 'Vérification rapide', es_MX: '' },
              { en_US: 'Remove', fr_CA: 'Retrait', es_MX: '' },
              { en_US: 'Reset', fr_CA: 'Réaménagement', es_MX: '' },
              { en_US: 'SS Assist', fr_CA: 'Assistance UtiliSource', es_MX: '' },
              { en_US: 'Standard Holdover', fr_CA: 'Prolongation standard', es_MX: '' },
            ],
          },
          cycle_id: {
            label: {
              en_US: 'Cycle ID',
              fr_CA: 'ID du cycle',
              es_MX: 'ID de ciclo',
            },
            type: 'number',
          },
          task_start_date: {
            label: {
              en_US: 'Task Start Date',
              fr_CA: 'Date de début de la tâche',
              es_MX: 'Fecha de inicio de la tarea',
            },
            type: 'date',
          },
          task_end_date: {
            label: {
              en_US: 'Task End Date',
              fr_CA: 'Date de fin de tâche',
              es_MX: 'Fecha de finalización de la tarea',
            },
            type: 'date',
          },
          product_line_id: {
            label: {
              en_US: 'Product Line ID',
              fr_CA: '',
              es_MX: '',
            },
            type: 'number',
          },
          product_line: {
            label: {
              en_US: 'Product Line',
              fr_CA: 'Gamme de produits',
              es_MX: 'Línea de producto',
            },
            type: 'string',
          },
          artwork_id: {
            label: {
              en_US: 'Artwork ID',
              fr_CA: 'ID visuel',
              es_MX: 'Identificación de la obra de arte',
            },
            type: 'string',
          },
          artwork: {
            label: { en_US: 'Artwork Description', es_MX: '', fr_CA: 'Description du visuel' },
            type: 'string',
          },
          kit: {
            label: { en_US: 'Kit Number', fr_CA: 'Numéro de kit', es_MX: '' },
            type: 'string',
          },
          ad_type: {
            label: { en_US: 'Ad Type', fr_CA: 'Type de visuels', es_MX: 'Tipo de anuncio' },
            type: 'string',
          },
          ad_quantity: {
            label: {
              en_US: 'Ad quantity',
              fr_CA: 'Qté de visuels',
              es_MX: 'Cantidad de anuncios',
            },
            type: 'number',
          },
          special_instructions: {
            label: { en_US: 'Special Instructions', es_MX: '', fr_CA: '' },
            type: 'string',
          },
          task_status_id: {
            label: { en_US: 'Task Status ID', es_MX: '', fr_CA: '' },
            type: 'number',
          },
          task_status: {
            label: { en_US: 'Task Status', fr_CA: 'État de la tâche', es_MX: 'Estado de la tarea' },
            type: 'string',
            options: [
              { en_US: 'Open', fr_CA: 'Ouvert', es_MX: '' },
              { en_US: 'Completed', fr_CA: 'Fermé', es_MX: '' },
              { en_US: 'Bulk', fr_CA: 'En bloc', es_MX: '' },
            ],
          },
          task_description: {
            label: {
              en_US: 'Task Description',
              fr_CA: 'Description de la tâche',
              es_MX: 'Descripción de la tarea',
            },
            type: 'string',
          },
          est_task_duration: {
            label: {
              en_US: 'Est Task Duration',
              fr_CA: 'Durée prévue',
              es_MX: 'Duración tarea',
            },
            type: 'time',
          },
          placement_section: {
            label: {
              en_US: 'Placement Section',
              fr_CA: 'Section de placement',
              es_MX: 'Sección de colocación',
            },
            type: 'string',
            options: [
              {
                en_US: 'ARRIVAL',
                fr_CA: 'ARRIVÉE',
                es_MX: '',
              },
              {
                en_US: 'MERCHANDISING',
                fr_CA: 'MISE EN MARCHÉ',
                es_MX: '',
              },
              {
                en_US: 'ALCOHOLIC BEVERAGES (Do not use)',
                fr_CA: 'BOISSONS ALCOOLISÉES (ne pas utiliser)',
                es_MX: '',
              },
              {
                en_US: 'BABY NEEDS',
                fr_CA: 'BÉBÉS',
                es_MX: '',
              },
              {
                en_US: 'BAKERY',
                fr_CA: 'BOULANGERIE',
                es_MX: '',
              },
              {
                en_US: 'BAKING ITEMS',
                fr_CA: 'PÂTISSERIE',
                es_MX: '',
              },
              {
                en_US: 'BEAUTY ITEMS',
                fr_CA: 'BEAUTÉ',
                es_MX: '',
              },
              {
                en_US: 'BREAD AND BUNS',
                fr_CA: 'PAINS ET BRIOCHES',
                es_MX: '',
              },
              {
                en_US: 'BREAKFAST FOODS',
                fr_CA: 'POUR LE PETIT-DÉJEUNER',
                es_MX: '',
              },
              {
                en_US: 'CANNED GOODS',
                fr_CA: 'PRODUITS EN CONSERVE',
                es_MX: '',
              },
              {
                en_US: 'CANNED MEATS AND FISH',
                fr_CA: 'VIANDES ET POISSONS EN CONSERVE',
                es_MX: '',
              },
              {
                en_US: 'CARTS',
                fr_CA: 'CHARIOTS',
                es_MX: '',
              },
              {
                en_US: 'CHECKOUT',
                fr_CA: 'CAISSE DE SORTIE',
                es_MX: '',
              },
              {
                en_US: 'CLEANING SUPPLIES',
                fr_CA: 'PRODUITS DE NETTOYAGE',
                es_MX: '',
              },
              {
                en_US: 'CONDIMENTS',
                fr_CA: 'CONDIMENTS',
                es_MX: '',
              },
              {
                en_US: 'COOKIES/CRACKERS',
                fr_CA: 'BISCUITS & CRAQUELINS',
                es_MX: '',
              },
              {
                en_US: 'END OF STORE VISIT',
                fr_CA: 'FIN DE LA VISITE EN MAGASIN',
                es_MX: '',
              },
              {
                en_US: 'FEMININE NEEDS',
                fr_CA: 'HYGIÈNE FÉMININE',
                es_MX: '',
              },
              {
                en_US: 'FLOOR',
                fr_CA: 'PLANCHER',
                es_MX: '',
              },
              {
                en_US: 'FRESH MEAT',
                fr_CA: 'VIANDE FRAÎCHE',
                es_MX: '',
              },
              {
                en_US: 'FRONT OF STORE',
                fr_CA: 'AVANT DU MAGASIN',
                es_MX: '',
              },
              {
                en_US: 'FROZEN FOODS',
                fr_CA: 'ALIMENTS SURGELÉS',
                es_MX: '',
              },
              {
                en_US: 'GENERAL FOOD ITEMS',
                fr_CA: 'PRODUITS ALIMENTAIRES GÉNÉRAUX',
                es_MX: '',
              },
              {
                en_US: 'GENERAL MERCHANDISE (NON FOOD ITEMS)',
                fr_CA: 'MARCHANDISE GÉNÉRALE (ARTICLES NON ALIMENTAIRES)',
                es_MX: '',
              },
              {
                en_US: 'HEALTH ITEMS',
                fr_CA: 'SANTÉ',
                es_MX: '',
              },
              {
                en_US: 'HOT BEVERAGES',
                fr_CA: 'BOISSONS CHAUDES',
                es_MX: '',
              },
              {
                en_US: 'OILS/SHORTENING',
                fr_CA: 'HUILES/MATIÈRES GRASSES',
                es_MX: '',
              },
              {
                en_US: 'PACKAGED DINNERS',
                fr_CA: 'DÎNERS PRÉEMBALLÉS',
                es_MX: '',
              },
              {
                en_US: 'PACKAGED SIDES',
                fr_CA: "PLATS D'ACCOMPAGNEMENTS PRÉEMBALLÉS",
                es_MX: '',
              },
              {
                en_US: 'PAPER GOODS',
                fr_CA: 'PAPETERIE',
                es_MX: '',
              },
              {
                en_US: 'PASTA',
                fr_CA: 'PÂTES',
                es_MX: '',
              },
              {
                en_US: 'PEANUT BUTTER AND JELLIES',
                fr_CA: "BEURRE D'ARACHIDES ET CONFITURES",
                es_MX: '',
              },
              {
                en_US: 'PET NEEDS',
                fr_CA: 'ARTICLES POUR ANIMAUX DE COMPAGNIE',
                es_MX: '',
              },
              {
                en_US: 'PHARMACY',
                fr_CA: 'PHARMACIE',
                es_MX: '',
              },
              {
                en_US: 'PREPARED FOODS',
                fr_CA: 'ALIMENTS PRÉPARÉS',
                es_MX: '',
              },
              {
                en_US: 'PRODUCE',
                fr_CA: 'FRUITS & LÉGUMES',
                es_MX: '',
              },
              {
                en_US: 'REFRIGERATED FOODS',
                fr_CA: 'ALIMENTS RÉFRIGÉRÉS',
                es_MX: '',
              },
              {
                en_US: 'SALAD TOPPINGS',
                fr_CA: 'GARNITURES POUR SALADE',
                es_MX: '',
              },
              {
                en_US: 'SALTY SNACKS',
                fr_CA: 'COLLATIONS SALÉES',
                es_MX: '',
              },
              {
                en_US: 'SHELF STABLE JUICE DRINKS',
                fr_CA: 'JUS EN TABLETTE',
                es_MX: '',
              },
              {
                en_US: 'SODA/WATER',
                fr_CA: 'BOISSONS GAZEUSES/EAU',
                es_MX: '',
              },
              {
                en_US: 'START OF STORE VISIT',
                fr_CA: 'DÉBUT DE LA VISITE EN MAGASIN',
                es_MX: '',
              },
              {
                en_US: 'SWEET SNACKS',
                fr_CA: 'COLLATIONS SUCRÉES',
                es_MX: '',
              },
              {
                en_US: 'TRASH/STORAGE BAGS/CONTAINERS',
                fr_CA: 'SACS-POUBELLE/RANGEMENT/CONTENANTS',
                es_MX: '',
              },
              {
                en_US: 'FC AUDIT',
                fr_CA: 'VÉRIFICATION FC',
                es_MX: '',
              },
              {
                en_US: 'INTERNATIONAL',
                fr_CA: 'INTERNATIONAL',
                es_MX: '',
              },
              {
                en_US: 'PRICE POP',
                fr_CA: 'PRICE POP',
                es_MX: '',
              },
              {
                en_US: 'OFFICE SUPPLIES',
                fr_CA: 'FOURNITURES DE BUREAU',
                es_MX: '',
              },
              {
                en_US: 'MAILING/SHIPPING SUPPLIES',
                fr_CA: "FOURNITURES D'EXPÉDITION",
                es_MX: '',
              },
              {
                en_US: 'ELECTRONICS / ACCESSORIES',
                fr_CA: 'ÉLECTRONIQUE / ACCESSOIRES',
                es_MX: '',
              },
              {
                en_US: 'PAPER/OFFICE',
                fr_CA: 'PAPETERIE/BUREAU',
                es_MX: '',
              },
              {
                en_US: 'FURNITURE',
                fr_CA: 'MEUBLES',
                es_MX: '',
              },
              {
                en_US: 'WRITING INSTRUMENTS',
                fr_CA: "INSTRUMENTS D'ÉCRITURE",
                es_MX: '',
              },
              {
                en_US: 'GENERAL MERCHANDISE - OFFICE',
                fr_CA: 'MARCHANDISES GÉNÉRALES - BUREAU',
                es_MX: '',
              },
              {
                en_US: 'PRE-PACKAGED MEAT',
                fr_CA: 'CHARCUTERIE',
                es_MX: '',
              },
              {
                en_US: 'PACKAGED SIDES/DINNERS',
                fr_CA: "PLATS D'ACCOMPAGNEMENT / REPAS",
                es_MX: '',
              },
              {
                en_US: 'HEALTH / BEAUTY ITEMS',
                fr_CA: 'SANTÉ & BEAUTÉ',
                es_MX: '',
              },
              {
                en_US: 'CANNED GOODS/CANNED MEATS AND FISH',
                fr_CA: 'ALIMENTS EN CONSERVE/VIANDES ET POISSONS EN CONSERVE',
                es_MX: '',
              },
              {
                en_US: 'BAKING ITEMS/OILS/SHORTENING',
                fr_CA: 'PÂTISSERIE/HUILES/MATIÈRE GRASSE',
                es_MX: '',
              },
              {
                en_US: 'SEAFOOD',
                fr_CA: 'FRUITS DE MER',
                es_MX: '',
              },
              {
                en_US: 'DELI',
                fr_CA: 'CHARCUTERIE',
                es_MX: '',
              },
              {
                en_US: 'FLORAL',
                fr_CA: 'FLEURS',
                es_MX: '',
              },
              {
                en_US: 'BEER',
                fr_CA: 'BIÈRE',
                es_MX: '',
              },
              {
                en_US: 'WINE',
                fr_CA: 'VIN',
                es_MX: '',
              },
              {
                en_US: 'SPIRITS',
                fr_CA: 'SPIRITS',
                es_MX: '',
              },
              {
                en_US: 'COUPON ZONE (CANADA ONLY)',
                fr_CA: 'ZONE DE BONS (UNIQUEMENT AU CANADA)',
                es_MX: '',
              },
              {
                en_US: 'SECURITY PEDESTALS',
                fr_CA: 'SECURITY PEDESTALS',
                es_MX: '',
              },
              {
                en_US: 'BASKET LINERS',
                fr_CA: 'FR_BASKET LINERS',
                es_MX: '',
              },
              {
                en_US: 'DEPARTURE',
                fr_CA: 'DÉPART',
                es_MX: '',
              },
            ],
          },
          space_id: {
            label: { en_US: 'Space ID', fr_CA: '', es_MX: '' },
            type: 'number',
          },
          previous_rai: {
            label: { en_US: 'Previous RAI', es_MX: '', fr_CA: '' },
            type: 'string',
          },
          previous_inventory_item: {
            label: { en_US: 'Previous Inventory Item', es_MX: '', fr_CA: '' },
            type: 'string',
          },
          previous_placement: {
            label: { en_US: 'Previous Placement', es_MX: '', fr_CA: '' },
            type: 'string',
          },
          job_id: {
            label: { en_US: 'Job ID', es_MX: '', fr_CA: '' },
            type: 'number',
          },
          wave_id: {
            label: { en_US: 'Wave ID', es_MX: '', fr_CA: '' },
            type: 'number',
          },
          art_description: {
            label: { en_US: 'Art Description', es_MX: '', fr_CA: '' },
            type: 'string',
          },
          job_description: {
            label: { en_US: 'Job Description', es_MX: '', fr_CA: '' },
            type: 'string',
          },
          job_name: {
            label: { en_US: 'Job Name', es_MX: '', fr_CA: 'Nom du projet' },
            type: 'string',
          },
          attachment: {
            label: {
              en_US: 'Attachment',
              fr_CA: 'Pièces jointes',
              es_MX: 'Archivo adjunto',
            },
            type: 'string',
          },
          previous_ad_quantity: {
            label: { en_US: 'Previous Ad quantity', fr_CA: '', es_MX: '' },
            type: 'number',
          },
          supplies_sent_to: {
            label: { en_US: 'Supplies Sent To', es_MX: '', fr_CA: '' },
            type: 'string',
          },
          recap: {
            label: { en_US: 'Recap Preview', es_MX: '', fr_CA: 'Aperçu du récapitulatif' },
            type: 'string',
          },
          territory: {
            label: { en_US: 'Territory', fr_CA: 'Territoire', es_MX: 'Territorio' },
            type: 'string',
          },
          image_required: {
            label: { en_US: 'Image Required', fr_CA: 'Image requise', es_MX: 'Imagen requerida' },
            type: 'boolean',
          },
          test_indicator: {
            label: { en_US: 'MPT', fr_CA: 'MPT', es_MX: '' },
            type: 'boolean',
          },
          //for dev work only
          bulk_updated: {
            label: { en_US: '', fr_CA: '', es_MX: '' },
            type: 'boolean',
            hide: true,
            hideInFilters: true,
          },
        },
      },
      data: taskData.value,
    }
  })

  /** Table format for task index page. */
  const taskIndex = computed(() => {
    const taskIndexKeys = [
      'attachment',
      'store_name',
      'store_number',
      'task_id', //keep, need for routing. Hide key below
      'artwork_id',
      'artwork_description',
      'task_description',
      'work_type',
      'task_type',
      'test_indicator', //aka mpt
      'product_line',
      'job_name',
      'orderline_id',
      'kit',
      'ad_type',
      'store_id',
      'cycle_id',
      'task_start_date',
      'task_end_date',
      // 'order_sales_id',
      'order_type',
    ]

    const taskIndexData: any = []
    for (let index = 0; index < taskData.value.length; index++) {
      const row = taskData.value[index]
      const newRow: any = { _index: index }
      for (const key of taskIndexKeys) {
        newRow[key] = row[key]
      }
      taskIndexData.push(newRow)
    }

    //intermediate result before setting default condition
    const transformedNoDef: Table = {
      meta: subsetMetaByKeys(taskTable.value.meta, taskIndexKeys, {
        renameKeys: [
          {
            from: 'task_id',
            to: {
              label: {
                en_US: 'Task ID',
                fr_CA: 'ID tâche',
                es_MX: 'ID tarea',
              },
              type: 'number',
              hide: true,
              hideInFilters: true,
            },
          },
        ],
      }),
      data: taskIndexData,
    }

    const transformed = setDefaultConditions(transformedNoDef, [
      {
        col: 'task_type',
        operation: 'isAnyOf',
        value: [
          { en_US: 'Install', fr_CA: 'Installation', es_MX: '' }[locale.value],
          { en_US: 'Other', fr_CA: 'Autre', es_MX: '' }[locale.value],
        ],
      },
    ])

    const returnTable = setGroupSort(transformed, {
      // groupBy: 'placement_section',
      sortBy: 'task_end_date',
    })

    return returnTable
  })
  /**Table format for picklist on task index page */
  const pickListData = computed(() => {
    return useTransformStore(taskTable.value).subset([
      'task_id',
      'product_line',
      'artwork_id',
      'task_type',
      'orderline_id',
      'placement_section',
      'kit_number',
      'description',
      'ad_type',
      'ad_quantity',
      'task_start_date',
      'task_end_date',
    ])
  })

  /**Hash of open tasks */
  const tasksHash = async () => {
    const tasksToHash = taskData.value.filter((task) => !closedIDs.includes(task.task_status_id))
    return await useProgressiveHash(tasksToHash)
  }

  const getEditedTasks = computed(() => {
    // return taskData.value.filter((task) => closedIDs.includes(task.task_status_id))
    return taskData.value.filter((task) => task.edited && !task.exclude_from_sync)
  })

  //Logic to determine if reps can start tasks is based on
  const canStartTask = (task_id: string | number) => {
    if (typeof task_id === 'string') {
      task_id = parseInt(task_id)
    }
    return !payloadIds.value.task_ids.includes(task_id)
  }

  const store = storeToRefs(useStore())
  const locale = computed(() => store.user.value.language as LanguageChoice)

  const translateTaskType = (task_type_id: number | string, language: LanguageChoice = 'en_US') => {
    return taskTypeTranslations[String(task_type_id)]
      ? taskTypeTranslations[String(task_type_id)][language]
      : undefined
  }
  return {
    getTaskByTaskId,
    taskTable,
    taskData,
    addTask,
    setTasks,
    removeTasks,
    resetTask,
    updateTask,
    updateTaskByMerge,
    updateRAI,
    taskIndex,
    pickListData,
    tasksHash,
    getEditedTasks,
    close,
    undoTaskInvChanges,
    taskHardwareDeductionCodes,
    resetStoreState,
    triggerTaskDataRef,
    getTaskIndex,
    translateTaskType,
    canStartTask,
  }
})
/**
 * Tasks that are present from data sync for a task
 */
export interface Task {
  id: string | number
  ad_quantity: string | number
  ad_type: string | number
  artwork_id: string | number
  artwork: string | number
  attachment: string | number
  coupon_value: string | number
  cycle_id: string | number
  est_task_duration: string | number
  execution_latitude: string | number
  execution_longitude: string | number
  /**for completed tasks, this is used to send only tasks that were completed
   * with the "include recap" toggle as true*/
  include_recap?: boolean
  image_required: string | number
  initial_image_rai: string | number
  item: string | number
  job_description: string | number
  job_name: string | number
  kit: string | number
  maintain_image_rai: string | number
  order_sales_id: string | number
  order_type: string | number
  order_type_id: string | number
  orderline_id: string | number
  placement_instruction1: string | number
  placement_instruction2: string | number
  placement_instruction3: string | number
  placement_section: string | number
  prev_rai_code_id: string | number
  previous_ad_quantity: string | number
  previous_placement_id: string | number
  previous_placement: string | number
  previous_rai: string | number
  prev_item_installed: string | number
  product_line_description: string | number
  product_line_id: string | number
  product_line: string | number
  project_code: string | number
  quantity: string | number
  recap_id: string | number
  recap: string | number
  remove_image_rai: string | number
  revisit_task_flag: string | number
  space_id: string | number
  special_instructions: string | number
  store_id: string | number
  store_name: string | number
  store_number: string | number
  sub_task_id: string | number
  supplies_sent_to: string | number
  task_description: string | number
  task_end_date: string | number
  task_id: string | number
  task_start_date: string | number
  task_status_id: string | number
  task_status: string | number
  task_type_id: string | number
  task_type: string | number
  test_indicator: string | number
  territory: string | number
  work_type_id: string | number
  work_type: string | number
  previous_execution_timestamp_tz: string | number
  revisit?: boolean
  exclude_from_sync?: boolean
  original_state?: Task
}

/**
 * Task type translations. The indexes are the task_type_id.
 */
const taskTypeTranslations: { [task_type_id: string]: { [T in LanguageChoice]: string } } = {
  '7': { en_US: 'Arrival', fr_CA: 'Arrivée', es_MX: '' },
  '6': { en_US: 'Audit', fr_CA: 'Vérification', es_MX: '' },
  '10': { en_US: 'Cut in', fr_CA: 'Insertion', es_MX: '' },
  '8': { en_US: 'Departure', fr_CA: 'Départ', es_MX: '' },
  '11': { en_US: 'Display Building', fr_CA: 'Assemblage de présentoir', es_MX: '' },
  '12': { en_US: 'General Maintenance', fr_CA: 'Entretien général', es_MX: '' },
  '1': { en_US: 'Install', fr_CA: 'Installation', es_MX: '' },
  '13': { en_US: 'IRC', fr_CA: 'Bon à réduction instantanée', es_MX: '' },
  '4': { en_US: 'Maintenance', fr_CA: 'Entretien', es_MX: '' },
  '14': { en_US: 'Mystery Ship', fr_CA: 'Achat mystère', es_MX: '' },
  '15': { en_US: 'Other', fr_CA: 'Autre', es_MX: '' },
  '16': { en_US: 'POS', fr_CA: 'Publicité sur le lieu de vente', es_MX: '' },
  '2': { en_US: 'Premium Holdover', fr_CA: 'Prolongation du client', es_MX: '' },
  '17': { en_US: 'Product Replenishment', fr_CA: 'Réassort de produits', es_MX: '' },
  '18': { en_US: 'Quick Audit', fr_CA: 'Vérification rapide', es_MX: '' },
  '5': { en_US: 'Remove', fr_CA: 'Retrait', es_MX: '' },
  '19': { en_US: 'Reset', fr_CA: 'Réaménagement', es_MX: '' },
  '20': { en_US: 'SS Assist', fr_CA: 'Assistance UtiliSource', es_MX: '' },
  '3': { en_US: 'Standard Holdover', fr_CA: 'Prolongation standard', es_MX: '' },
}
